/**
 * Textos y constantes de las páginas públicas de torneos123.com (rediseño
 * Eduardo 2026-09-29). Todo lo que se promete aquí existe en el sistema; si
 * cambia una regla del producto, se cambia el texto aquí.
 */
import type { IconName } from "../../components/icons";

/** Avisos automáticos por WhatsApp a jugadores. Existen en el sistema, pero en
 * producción solo salen a números internos (WHATSAPP_ALLOWED_RECIPIENTS).
 * Eduardo 2026-09-29: «todavía no» se abren, así que la página no los promete.
 * El día que se abra el candado, se cambia a true y los textos que dependen de
 * esta constante vuelven a mencionar WhatsApp. Lo que sí funciona hoy (enlace
 * del grupo solo a quien pagó, copiar el comunicado al grupo) se menciona
 * siempre. */
export const WHATSAPP_AUTO_LIVE: boolean = false;

/** Precio y promoción, textos exactos en todo el sitio (sin IVA, por decisión
 * de Eduardo). */
export const PRECIO_TEXTO = "$99 MXN por jugador inscrito, por torneo";
export const PRIMER_TORNEO_TEXTO = "Haz tu primer torneo gratis";

/** Compromiso de respuesta al pedir demo (null = no se promete tiempo). */
export const RESPUESTA_COMPROMISO: string | null = null;

/** Mínimo de torneos abiertos para mostrar la prueba en vivo. */
export const PRUEBA_MINIMA = 2;

/** Nombres que no se muestran como prueba en vivo. */
export const NOMBRES_DE_PRUEBA = /prueba|test|demo|copia|ensayo/i;

export interface PainItem {
  before: string;
  after: string;
}

export const PAIN_ITEMS: PainItem[] = [
  {
    before: "Inscripciones regadas entre un formulario, el chat y una libreta.",
    after: "Un solo enlace: convocatoria, reglamento, datos y pago en 4 pasos. La CURP en PDF llena el formulario sola.",
  },
  {
    before: "Revisar transferencias una por una contra el estado de cuenta.",
    after: "El jugador paga con Mercado Pago, su inscripción se marca pagada sola y a ti te llega el aviso.",
  },
  {
    before: "Revisar actas para saber si un jugador es de su categoría.",
    after: "Categorías por año de nacimiento y rama: quien no corresponde no se puede inscribir.",
  },
  {
    before: "Armar cuadros en Excel y aguantar el «¿por qué me tocó con él?».",
    after: "Cuadro en un clic con siembra estándar internacional y un acta del sorteo que cada jugador puede ver.",
  },
  {
    before: "Contestar «¿a qué hora juego?» todo el fin de semana.",
    after: WHATSAPP_AUTO_LIVE
      ? "Cada jugador ve su horario, cancha y rival en su celular y recibe recordatorio por WhatsApp antes de jugar."
      : "Cada jugador ve su horario, cancha y rival en su celular y recibe un recordatorio antes de jugar.",
  },
  {
    before: "Terminar el torneo sin saber cuánto cobraste ni cuánto falta.",
    after: "Un tablero con lo cobrado, lo pendiente, las comisiones y lo que te queda.",
  },
];

export interface StepItem {
  title: string;
  copy: string;
}

export const HOW_IT_WORKS: StepItem[] = [
  { title: "Pides tu demo.", copy: "Te enseñamos el sistema y resolvemos tus dudas sobre tu torneo." },
  {
    title: "Creamos tu cuenta y conectas tu Mercado Pago.",
    copy: "En unos clics; lo que pagan tus jugadores llega directo a tu cuenta.",
  },
  {
    title: "Armas tu torneo en 10 pasos guiados.",
    copy: "Datos, reglamento, convocatoria, inscripciones, formato, grupos, cuadro y calendario. No te deja avanzar con algo incompleto y, si te interrumpen, retomas donde te quedaste.",
  },
  {
    title: "Compartes tu enlace.",
    copy: "Tus jugadores se inscriben y pagan; tú programas, capturas resultados y cierras con las posiciones finales.",
  },
];

export interface SceneCopy {
  kicker: string;
  title: string;
  lead: string;
  bullets: string[];
}

export const SCENES = {
  registration: {
    kicker: "Inscripción",
    title: "Un enlace y tu torneo se llena solo",
    lead: "Compartes tu enlace de inscripción y cada jugador ve tu convocatoria, acepta el reglamento, llena sus datos y paga desde el celular. Tú no capturas nada a mano.",
    bullets: [
      "Sube su CURP en PDF y el formulario se llena solo: nombre, fecha de nacimiento, género y estado.",
      "Cada categoría acepta solo a quien le corresponde por año de nacimiento y rama.",
      "Se inscribe a varias categorías y paga una sola orden.",
      "Precios distintos para general, socios o profesores, con los nombres que tú elijas.",
      "Los papás inscriben a todos sus hijos desde una sola cuenta.",
    ],
  },
  payments: {
    kicker: "Cobro",
    title: "Cobra en línea: el dinero llega a tu Mercado Pago",
    lead: "Conectas tu cuenta de Mercado Pago en unos clics. Cuando un jugador paga, su inscripción se marca pagada sola, a él le llega su confirmación y a ti el aviso «Pago recibido». Tú lo apruebas con un clic.",
    bullets: [
      "Al aprobarlo, le llega solo su correo de bienvenida con usuario y contraseña.",
      "Recordatorio de pago con un botón para quien no ha pagado.",
      "Reembolsos totales o parciales desde la plataforma; si pagó con Mercado Pago, el dinero le regresa solo.",
      "Dashboard financiero: pagos recibidos, pendiente por cobrar, comisiones y lo que te queda.",
      "El enlace de tu grupo de WhatsApp solo le llega a quien ya pagó.",
    ],
  },
  formats: {
    kicker: "Formatos y sorteo",
    title: "El formato que quieras. El cuadro, en un clic.",
    lead: "Eliges el formato de cada categoría y el sistema arma grupos, cuadros y liguillas con las reglas del tenis. Haces el sorteo a la vista de todos y cada jugador consulta el acta en su celular.",
    bullets: [
      "Eliminación directa con sembrados en sus líneas oficiales, pases directos o ronda preliminar cuando no son 8, 16 o 32.",
      "Grupos de todos contra todos repartidos en serpentina; al terminar los grupos, la liguilla se llena sola.",
      "El ganador avanza solo y el partido por el 3er lugar aparece en cuanto terminan las semifinales.",
      "Desempates tipo ITF, liga por puntos o el orden que tú elijas.",
      "Escaleras de temporada y torneos por equipos con series de singles y dobles.",
    ],
  },
  courts: {
    kicker: "Calendario de canchas",
    title: "Programa todo el día en un clic, sin empalmes de cancha",
    lead: "Eliges fecha, hora de inicio, duración y número de canchas, y el sistema reparte los partidos pendientes en orden de rondas, sin dos partidos en la misma cancha a la misma hora. Tú revisas y ajustas lo que quieras.",
    bullets: [
      "No te deja poner dos partidos en la misma cancha a la misma hora ni citar a un jugador a dos partidos a la misma hora, y te dice con qué choca.",
      "Cuadrícula por cancha y horario: tocas un partido, tocas un hueco y queda programado.",
      "Bloqueas canchas por mantenimiento, comida o uso del club.",
      "¿Llovió? Mueves el partido, eliges el motivo y los dos jugadores reciben el aviso con la nueva hora.",
      "Varias sedes, cada una con sus canchas, y el rol de juegos en PDF completo o por jornada.",
    ],
  },
  results: {
    kicker: "Resultados",
    title: "Capturas el marcador y todo se actualiza solo",
    lead: "Desde tu celular capturas cada resultado en segundos. El ganador pasa a su siguiente partido y la tabla se reordena al instante.",
    bullets: [
      "Reglas del tenis: muerte súbita, súper muerte súbita y retiros con marcador parcial.",
      "Si lo activas, tus jugadores reportan su marcador y tú solo lo apruebas o rechazas; te llega una notificación con cada reporte.",
      "¿Te equivocaste? Despublicas el resultado y el cuadro regresa sin descuadrarse.",
      "Si alguien se da de baja, eliges si sus partidos pendientes se dan por W.O. o se cancelan.",
      "Tabla de posiciones en vivo, lista para imprimir.",
    ],
  },
  players: {
    kicker: "Jugadores informados",
    title: "Dejas de contestar «¿a qué hora juego?»",
    lead: "Cada jugador tiene Torneos123 en su celular con su próximo partido, rival, hora y cancha, además de su cuadro, su tabla y tus avisos. Se instala desde el navegador, sin tiendas de aplicaciones.",
    bullets: [
      WHATSAPP_AUTO_LIVE
        ? "Recordatorio por WhatsApp un día antes y una hora antes de cada partido programado."
        : "Recordatorio en su celular un día antes y una hora antes de cada partido programado.",
      "Avisos automáticos cuando se confirma su pago, sale el sorteo o cambias su horario.",
      "Comunicados para todo el torneo, un grupo o un jugador, con plantillas listas.",
      "Tu grupo de WhatsApp en orden: copias cada comunicado para pegarlo en el grupo con un botón.",
    ],
  },
  closing: {
    kicker: "Cierre y ranking",
    title: "Cierra con campeones y un ranking que hace volver a tus jugadores",
    lead: "Cierras el torneo con un clic: quedan guardados campeón y subcampeón de cada categoría y todo pasa a solo lectura.",
    bullets: [
      "Ranking propio de tu club o circuito, con tus reglas de puntos, que publicas con un enlace.",
      "Cada jugador ve su historial, su récord y sus logros en su perfil.",
      "Comunicado de campeones listo para enviar.",
    ],
  },
} satisfies Record<string, SceneCopy>;

/** Canales de los avisos al jugador (D4). */
export const NOTICE_CHANNELS: { label: string; live: boolean }[] = [
  { label: "WhatsApp", live: WHATSAPP_AUTO_LIVE },
  { label: "Notificación en el celular", live: true },
  { label: "Correo, si lo activas", live: true },
].filter((channel) => channel.live);

export const NOTICE_FINE_PRINT =
  "Los avisos llegan a jugadores con cuenta en torneos ya publicados, por los canales que actives en cada torneo; las notificaciones del celular, a quien las acepta (en iPhone, después de agregar Torneos123 a su pantalla de inicio). El recordatorio del día anterior sale si el partido se programó con al menos 20 horas de anticipación.";

export interface IconItem {
  icon: IconName;
  title: string;
  copy: string;
}

export const BRAND_ITEMS: IconItem[] = [
  { icon: "book", title: "Convocatoria", copy: "Convocatoria en PDF con tu logo, armada sola con fechas, sede, costo y categorías." },
  { icon: "bracket", title: "Cuadro impreso", copy: "Lista de inscritos y cuadro impreso en una hoja carta, con tu logo." },
  { icon: "ticket", title: "Credencial", copy: "Credencial digital con foto, tu logo y código QR que se verifica al escanearlo." },
  { icon: "megaphone", title: "Patrocinadores", copy: "Tu cartel y el banner de tu patrocinador en las pantallas del torneo que ven tus jugadores." },
];

export const MORE_FEATURES: IconItem[] = [
  { icon: "users", title: "Tu equipo, con permisos", copy: "Capturista, finanzas o coadministrador, sin prestar tu contraseña." },
  { icon: "clipboard", title: "Asistente de 10 pasos", copy: "No te deja avanzar con algo incompleto y retomas donde te quedaste." },
  { icon: "chart", title: "Semáforo por categoría", copy: "Ves qué le falta a cada categoría antes del día del torneo." },
  { icon: "lock", title: "Nada a medio armar", copy: "Tus jugadores ven grupos, cuadro y horarios hasta que publicas." },
  { icon: "ticket", title: "Inscripción de mostrador", copy: "El día del torneo das de alta, inscribes y marcas pagado en un solo paso." },
  { icon: "card", title: "Cobros sueltos", copy: "Generas un enlace de pago con el monto que quieras." },
  { icon: "trophy", title: "Directorio público", copy: "Si lo marcas como público, tu torneo aparece en torneos123.com con fechas, sede y precio." },
  { icon: "mail", title: "Correos automáticos", copy: "Inscripción recibida, pago confirmado y bienvenida con usuario y contraseña." },
  { icon: "megaphone", title: "Plantillas de comunicados", copy: "Calendario de la semana, clasificados, premiación y más, listas para usar." },
  { icon: "user", title: "Ve lo que ve tu jugador", copy: "Con un botón entras a su vista para resolver sus dudas." },
  { icon: "users", title: "Dobles sin enredos", copy: "Cada quien se inscribe y el sistema te sugiere las parejas; tú confirmas." },
  { icon: "wallet", title: "Precio por tipo de jugador", copy: "General, socio o profesor, con los nombres que tú elijas." },
];

export const TRUST_ITEMS: IconItem[] = [
  { icon: "wallet", title: "Tu dinero no pasa por nosotros.", copy: "Cada pago en línea llega directo a tu cuenta de Mercado Pago." },
  { icon: "check", title: "Pagas solo por jugador inscrito.", copy: "Sin mensualidad." },
  { icon: "book", title: "Ayuda en cada pantalla.", copy: "Botón de ayuda por sección y una guía completa del organizador." },
];

export interface FaqItem {
  q: string;
  a: string;
}

export const FAQ_ITEMS: FaqItem[] = [
  {
    q: "¿Quién paga los $99 por jugador?",
    a: "Se descuentan de lo que paga el jugador. Tú fijas el costo de inscripción; cuando el jugador paga en línea, Mercado Pago separa en automático $99 para Torneos123 y deposita el resto en tu cuenta. Si no quieres que salga de tu bolsa, inclúyelo en tu costo de inscripción. Si el jugador se inscribe a varias categorías en el mismo registro, se cobra una sola vez. Si tu torneo no cobra inscripción, no hay comisión.",
  },
  {
    q: "¿Cómo funciona «Haz tu primer torneo gratis»?",
    a: "Tu primer torneo en Torneos123 va sin comisión de Torneos123, sin importar cuántos jugadores tenga. Lo dejamos configurado así al crear tu cuenta, antes de que abras inscripciones. Mercado Pago sigue cobrando su propia comisión por cada pago en línea.",
  },
  {
    q: "¿El dinero llega a mi cuenta? ¿Y si algunos pagan en efectivo o por transferencia?",
    a: "Sí: conectas tu propia cuenta de Mercado Pago y cada pago en línea llega directo ahí; Torneos123 solo separa su comisión. Cuándo puedes disponer del dinero depende de tu cuenta de Mercado Pago. El día del torneo puedes inscribir en la mesa y marcar el pago en un solo paso. El cobro por transferencia con comprobante se activa por torneo con autorización de Torneos123; lo revisamos contigo en la demo.",
  },
  {
    q: "Mis jugadores (o sus papás) no son muy tecnológicos. ¿Lo van a poder usar? ¿Tienen que descargar una app?",
    a: "Se inscriben y pagan desde un enlace en el navegador de su celular, sin descargar nada de ninguna tienda. La CURP en PDF llena sus datos sola y un papá inscribe a todos sus hijos desde una sola cuenta. Para seguir su torneo pueden agregar Torneos123 a su pantalla de inicio; en iPhone, la app les explica cómo. Y si alguien se atora, con un botón ves la plataforma tal como la ve ese jugador.",
  },
  {
    q: "¿Sirve para torneos infantiles y juveniles?",
    a: "Sí. Configuras cada categoría por año de nacimiento y rama, y el sistema no deja inscribirse a quien no le corresponde. Recibes la foto y la CURP de cada jugador, en el formulario de inscripción puedes pedir los datos del tutor de los menores y los papás usan cuentas familiares.",
  },
  {
    q: "¿Sirve para pádel, pickleball, dobles y torneos por equipos?",
    a: "Sí. Eliges el deporte al crear cada torneo: en pádel las categorías son de parejas y en pickleball el marcador es a 11. También tienes dobles, escaleras de temporada y torneos por equipos con series de singles y dobles.",
  },
  {
    q: WHATSAPP_AUTO_LIVE ? "¿Les llegan avisos a los jugadores por WhatsApp?" : "¿Cómo se usa WhatsApp con mis jugadores?",
    a: WHATSAPP_AUTO_LIVE
      ? "Sí. En cada torneo eliges los canales: los avisos automáticos (recordatorio de partido un día y una hora antes, cambio de horario, pago confirmado y sorteo) llegan por WhatsApp al celular de cada jugador, además de la notificación en la app y, si lo activas, el correo. Y el enlace de tu grupo de WhatsApp solo le llega a quien ya pagó."
      : "Con tu grupo de WhatsApp: el enlace del grupo solo le llega a quien ya pagó y copias cada comunicado para pegarlo en el grupo con un botón. Los avisos automáticos (recordatorio de partido un día y una hora antes, cambio de horario, pago confirmado y sorteo) llegan a la app del jugador con notificación en su celular y, si lo activas, por correo.",
  },
];

export const DEMO_NEXT_STEPS: string[] = [
  RESPUESTA_COMPROMISO ? `Te contactamos ${RESPUESTA_COMPROMISO} para agendar tu demo.` : "Te contactamos para agendar tu demo.",
  "Te enseñamos el sistema y vemos juntos cómo organizarías tu torneo en Torneos123.",
  "Si te convence, creamos tu cuenta y haces tu primer torneo gratis.",
];

export const PAGE_META = {
  home: {
    title: "Torneos123 · Torneos de tenis, pádel y pickleball",
    description:
      "Encuentra torneos con inscripciones abiertas e inscríbete desde tu celular. ¿Organizas torneos? Inscripciones con pago en línea, cuadros y calendario de canchas.",
  },
  organizer: {
    title: "Organiza torneos de tenis, pádel y pickleball | Torneos123",
    description: `Inscripciones con pago en Mercado Pago, cuadros y grupos automáticos, calendario de canchas y avisos ${WHATSAPP_AUTO_LIVE ? "por WhatsApp " : ""}a tus jugadores. ${PRECIO_TEXTO}. ${PRIMER_TORNEO_TEXTO}.`,
  },
  tournaments: {
    title: "Torneos con inscripciones abiertas | Torneos123",
    description: "Busca torneos de tenis, pádel y pickleball e inscríbete desde tu celular.",
  },
  player: {
    title: "Torneos123 para jugadores",
    description: "Tu próximo partido, tu cuadro, tus resultados y tu credencial en tu celular.",
  },
  privacy: {
    title: "Aviso de privacidad | Torneos123",
    description: "Cómo tratamos tus datos personales en Torneos123.",
  },
} as const;

export type PublicPageKey = keyof typeof PAGE_META;
